import * as graph from '@microsoft/microsoft-graph-client';
import { ResponseType } from '@microsoft/microsoft-graph-client';
import { User } from '@microsoft/microsoft-graph-types';
import { MSGraphServiceErrorCodes } from '../models/MSGraphservice.types';
import { GraphAuthenticationProvider } from './GraphAuthenticationProvider';
import { BaseService } from './BaseService';
import { ResourceInfo } from '../models/IConfig';
import { IPublicClientApplication } from '@azure/msal-browser';

export class MSGraphService extends BaseService {
  private readonly graphClient: graph.Client;
  private userProfileCache: Map<string, User>;
  public constructor(resourceInfo: ResourceInfo, msalInstance: IPublicClientApplication) {
    super(resourceInfo, msalInstance);
    this.graphClient = graph.Client.initWithMiddleware({
      defaultVersion: 'v1.0',
      debugLogging: false,
      authProvider: new GraphAuthenticationProvider(resourceInfo.resourceId, msalInstance),
    });
    this.userProfileCache = new Map();
  }

  // TODO cache results for performance
  public async getPhotoForResource(userId: string): Promise<string | null> {
    try {
      const response = (await this.graphClient
        .api(`users/${userId}/photo/$value`)
        .responseType(ResponseType.RAW)
        .get()) as Response;

      if (!response.ok) {
        return null;
      }
      const base64Pic = await this.blobToBase64(await response.blob());
      return base64Pic;
    } catch (e) {
      return null;
    }
  }

  public async getMyPhoto(): Promise<string | null> {
    try {
      const response = (await this.graphClient.api(`me/photo/$value`).responseType(ResponseType.RAW).get()) as Response;
      if (!response.ok) {
        return null;
      }
      const base64Pic = await this.blobToBase64(await response.blob());
      return base64Pic;
    } catch (e) {
      return null;
    }
  }

  public async getMyDetails(): Promise<User | null> {
    try {
      const response = (await this.graphClient.api(`me`).responseType(ResponseType.JSON).get()) as User;
      return response;
    } catch (e) {
      return null;
    }
  }

  public async getUserDetails(userId: string): Promise<User | null> {
    if (this.userProfileCache.has(userId)) {
      return this.userProfileCache.get(userId) ?? null;
    }
    try {
      const response = (await this.graphClient.api(`users/${userId}`).responseType(ResponseType.JSON).get()) as User;
      this.userProfileCache.set(userId, response);
      return response;
    } catch (e) {
      //If resource if not found, throw exception, caller may want to handle it differently
      if (e?.code == MSGraphServiceErrorCodes.ResourceNotFound) {
        throw e;
      }
      return null;
    }
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (_) => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(blob);
    });
  }
}
